/**
 * SequenceWaitConnector — "⏱ Wait [N] days" connector between sequence blocks (§11-E).
 *
 * Renders a thin centered pill between two `SequenceBlockCard`s with an
 * inline-editable number of days.
 */

import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';

interface SequenceWaitConnectorProps {
  /** Current delay in days. */
  days: number;
  /** Called when the delay is changed. */
  onChange: (days: number) => void;
  /** Optional: disable editing (e.g. when sequence is active). */
  readOnly?: boolean;
}

export function SequenceWaitConnector({ days, onChange, readOnly }: SequenceWaitConnectorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(days));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(String(days));
  }, [days]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    const parsed = Math.max(0, Math.min(90, Math.round(Number(draft) || 0)));
    onChange(parsed);
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-center py-2">
      <div
        className="flex items-center gap-1.5 rounded-full border border-dashed border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)] px-3 py-1 text-[12px] vscode-text-secondary"
      >
        <Clock className="w-3 h-3" aria-hidden />
        <span>Wait</span>
        {editing && !readOnly ? (
          <input
            ref={inputRef}
            type="number"
            min={0}
            max={90}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                setDraft(String(days));
                setEditing(false);
              }
            }}
            className="w-10 text-center bg-transparent border-b border-emerald-500 outline-none vscode-text-primary font-medium"
          />
        ) : (
          <button
            type="button"
            disabled={readOnly}
            onClick={() => setEditing(true)}
            className="font-medium vscode-text-primary rounded px-1 hover:bg-[var(--vscode-bg-hover)] disabled:hover:bg-transparent disabled:cursor-default"
          >
            {days}
          </button>
        )}
        <span>{days === 1 ? 'day' : 'days'}</span>
      </div>
    </div>
  );
}
